/**
 * Feedback Modal Component
 * Modal dialog for adding/editing lesson feedback
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Feedback as FeedbackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { ReviewStatus } from '../../types';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (feedback: string) => void;
  initialFeedback?: string;
  lessonName?: string;
  reviewStatus?: ReviewStatus;
  loading?: boolean;
}

export default function FeedbackModal({
  open,
  onClose,
  onSave,
  initialFeedback = '',
  lessonName,
  reviewStatus,
  loading = false
}: FeedbackModalProps) {
  const [feedback, setFeedback] = useState(initialFeedback);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when initial feedback changes
  useEffect(() => {
    setFeedback(initialFeedback);
    setHasChanges(false);
  }, [initialFeedback]);

  // Track changes
  useEffect(() => {
    setHasChanges(feedback !== initialFeedback);
  }, [feedback, initialFeedback]);

  const handleSave = () => {
    onSave(feedback);
  };

  const handleCancel = () => {
    setFeedback(initialFeedback);
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FeedbackIcon color="primary" />
            <Typography variant="h6">Add Feedback</Typography>
          </Box>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {lessonName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {lessonName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {reviewStatus === ReviewStatus.REJECTED && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              This prediction has been rejected.
            </Typography>
            <Typography variant="body2">
              Please provide feedback explaining why this prediction was rejected to help improve future predictions.
            </Typography>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add notes, observations, or feedback about this prediction. This feedback will be saved with the lesson.
        </Typography>

        <TextField
          autoFocus
          multiline
          rows={8}
          fullWidth
          label="Feedback"
          placeholder="Enter your feedback, notes, or observations about this lesson prediction..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          variant="outlined"
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.9rem',
              lineHeight: 1.5,
            },
          }}
        />

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {feedback.length} characters
          </Typography>
          {hasChanges && (
            <Typography variant="caption" color="primary">
              • Changes not saved
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1, px: 3, pb: 3 }}>
        <Button onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={loading ? null : <SaveIcon />}
          disabled={loading || !hasChanges}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Saving...' : 'Save Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}